import React, { useState } from 'react';
import './ResearchProgress.css';
import { researchChecks, researchDepths } from '../lib/researchDepth';

const reasons = {
  depth_boundary: 'Экспресс завершён с ограничениями. Для проверки противоречий и пробелов можно продолжить глубже.',
  sufficient_result: 'Подготовлены ответы и сценарии по обязательным вопросам. Степень подтверждения показана для каждого результата.',
  no_progress: 'Дальнейшие попытки не дали новых существенных сведений. Пробелы отражены в результатах.',
  account_quota: 'Доступный объём закончился. Найденные материалы сохранены.',
  user_requested: 'Исследование завершено по вашей команде.',
  technical_failure: 'Продолжению помешали технические ошибки. Полученные материалы сохранены.',
};
const statusNames = {
  open: 'Исследуется', supported: 'Подтверждено', conditional: 'Подготовлен условный сценарий', unavailable: 'Данные недоступны', stalled: 'Остался пробел',
};

const isCheckPassed = check => ['passed', 'not_applicable'].includes(check?.status);
const countPassedChecks = (criterion, required) => required.filter(key => isCheckPassed(criterion.checks?.[key])).length;

function planLabel(description) {
  const text = (description || '').replace(/\s+/g, ' ').trim();
  const colon = text.indexOf(':');
  const heading = colon > 0 && colon < 90 ? text.slice(0, colon) : text;
  const short = heading.length > 68 ? heading.slice(0, 65).replace(/\s+\S*$/, '') + '…' : heading;
  return short.charAt(0).toUpperCase() + short.slice(1);
}

function ResearchChecklist({ criteria, completed, conditional, policy, workPlan }) {
  const [expanded, setExpanded] = useState(false);
  const required = policy?.required_checks || [];
  const totalChecks = workPlan?.total ?? criteria.length * required.length;
  const passedChecks = workPlan?.passed ?? criteria.reduce((total, criterion) => total
    + countPassedChecks(criterion, required), 0);
  const preparedQuestions = workPlan?.prepared_questions ?? criteria.filter(criterion => criterion.evidence_artifact_ids?.length).length;
  return <section className="chat-bubble research-plan" aria-label="План исследования">
    <div className="research-plan-header">
      <h2>План исследования</h2>
      <span className="research-plan-count" aria-label={`Выполнено: ${completed} из ${criteria.length}`}
        title={`Выполнено: ${completed} из ${criteria.length}`}>{completed} / {criteria.length}</span>
      {criteria.length > 0 && <button type="button" className="research-plan-toggle" aria-expanded={expanded}
        aria-label={expanded ? 'Краткий план' : 'Полный текст плана'} onClick={() => setExpanded(value => !value)}>
        {expanded ? 'Кратко' : 'Подробнее'}
      </button>}
    </div>
    {policy && <p className="research-plan-scope">
      <strong title={`Уровней проверки: ${required.length}`}>{researchDepths[policy.depth]?.label}</strong> · Проверки: {passedChecks} / {totalChecks}
      {preparedQuestions > 0 && <span> · Материалы: {preparedQuestions} / {criteria.length}</span>}
    </p>}
    {criteria.length === 0 ? <p className="text-muted">Составляю план исследования.</p>
      : <ul className={`research-plan-items${expanded ? ' is-expanded' : ''}`}>{criteria.map(criterion => {
        const done = ['supported', 'conditional'].includes(criterion.status);
        const passed = countPassedChecks(criterion, required);
        return <li key={criterion.id}>
          <label className="research-plan-item" title={criterion.description}>
            <input type="checkbox" checked={done} disabled aria-label={criterion.description} />
            <span className="research-plan-text">{expanded ? criterion.description : planLabel(criterion.description)}</span>
          </label>
          {!expanded && !done && passed > 0 && <small className="research-plan-tag"
            title={`Пройдено проверок: ${passed} из ${required.length}`}>{passed} / {required.length}</small>}
          {criterion.status === 'conditional' && <small className="research-plan-tag">С допущениями</small>}
          {['unavailable', 'stalled'].includes(criterion.status) && <small className="research-plan-tag is-warning">{statusNames[criterion.status]}</small>}
          {expanded && required.length > 0 && <ul className="research-plan-checks" aria-label={`Проверки: ${planLabel(criterion.description)}`}>
            {required.map(key => {
              const check = criterion.checks?.[key] || {};
              const passed = isCheckPassed(check);
              const execution = check.execution_status;
              const state = passed ? 'Выполнено'
                : ['running', 'waiting_tool'].includes(execution) ? 'В работе'
                : execution === 'done' ? 'Материал подготовлен, ожидает проверки'
                : execution === 'failed' ? 'Требует доработки' : 'Не выполнено';
              return <li key={key}>
                <label><input type="checkbox" disabled checked={passed} />{researchChecks[key] || check.label}</label>
                <small>{state}</small>
              </li>;
            })}
          </ul>}
        </li>;
      })}</ul>}
    {conditional > 0 && <p className="research-plan-note">С допущениями — требуется проверка фактов.</p>}
  </section>;
}

export default function ResearchProgress({ session, onContinue, busy = false, checklist = false }) {
  const research = session.research;
  if (!research) return null;
  const criteria = research.criteria || [];
  const depth = research.depth_policy?.depth;
  const checkNames = { open: 'Ожидает проверки', passed: 'Выполнено', not_applicable: 'Не требуется: есть обоснование' };
  const passed = criteria.filter(c => c.status === 'supported').length;
  const conditional = criteria.filter(c => c.status === 'conditional').length;
  const supportNames = { confirmed: 'подтверждены', partial: 'подтверждены частично', unavailable: 'не подтверждены', not_applicable: 'сценарий на допущениях' };
  const paused = ['paused', 'waiting_for_human'].includes(session.status);
  const finished = session.status === 'finished';
  const terminal = ['finished', 'cancelled', 'failed'].includes(session.status);
  const finalizing = session.current_phase === 'finalization' && !terminal;
  const title = finished
    ? research.result_completeness === 'complete' ? 'Исследование завершено' : 'Исследование завершено с ограничениями'
    : session.status === 'cancelled' ? 'Исследование отменено'
    : session.status === 'failed' ? 'Исследование остановлено'
    : finalizing ? 'Подготовка результатов' : 'Исследование';
  if (checklist) return <ResearchChecklist criteria={criteria} completed={passed + conditional} conditional={conditional}
    policy={research.depth_policy} workPlan={research.work_plan} />;
  return <section className="card" aria-label="Прогресс исследования" style={{ marginBottom: 16 }}>
    <h2 className="card-title">{title}</h2>
    {depth && <p><strong>Глубина: {researchDepths[depth]?.label || depth}</strong>. {researchDepths[depth]?.description}</p>}
    {criteria.length > 0 && <>
      <p>Подтверждено вопросов: {passed} из {criteria.length}.</p>
      {conditional > 0 && <p>Подготовлено условных сценариев: {conditional}. Их допущения требуют проверки.</p>}
      <details>
        <summary>Вопросы исследования</summary>
        <ul>{criteria.map(c => <li key={c.id}>
          {c.description} — {statusNames[c.status] || 'Исследуется'}
          {c.factual_support && <p>Фактические основания: {supportNames[c.factual_support] || 'требуют проверки'}.</p>}
          {c.scenario_readiness?.status === 'ready' && <p>Сценарий подготовлен. Вывод действует при указанных допущениях.</p>}
          {c.material_contradiction && <p>Есть существенное противоречие.</p>}
          {c.checks && <ul>{Object.entries(c.checks).map(([key, check]) => <li key={key}>
            {check.label}: {checkNames[check.status] || check.status}
            {check.status === 'not_applicable' && check.reason && ` — ${check.reason}`}
          </li>)}</ul>}
        </li>)}</ul>
      </details>
    </>}
    {finished && <p>{reasons[research.completion_reason]}</p>}
    {paused && <p>Работа приостановлена. Для продолжения потребуется доступный объём.</p>}
    {!terminal && !paused && !finalizing && <p className="text-muted">Работа продолжается, пока есть полезные следующие шаги и доступный объём. Предварительный результат появится после первого этапа.</p>}
    {finalizing && !paused && <p className="text-muted">Готовлю выбранные результаты по сохранённым материалам. Новых исследований на этом этапе не будет.</p>}
    {['cancelled', 'failed'].includes(session.status) && <p>Собранные материалы и сохранённый черновик доступны для просмотра.</p>}
    {finished && research.result_completeness === 'partial' && <p className="text-muted">Для дополнительного исследования отправьте новый запрос. Работа продолжится только по вашей команде.</p>}
    {finished && depth === 'express' && onContinue && <div className="session-actions" aria-label="Продолжить исследование глубже">
      <button className="btn btn-secondary" disabled={busy} onClick={() => onContinue('standard')}>Продолжить в стандарте</button>
      <button className="btn btn-primary" disabled={busy} onClick={() => onContinue('deep')}>Продолжить глубоко</button>
    </div>}
  </section>;
}
